/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import TaskDetailsTable from './TaskDetailsTable'
import Loader from '../../../../components/shared/Loader'
import { Task } from '../../../../services/api/types'
import { getTask } from '../../../../services/api/tasks'
import { getWorkflow } from '../../../../services/api/workflows'
import { usersApiService } from '../../../../services/api/users'
import { getFsiPath } from '../../../../services/api/files'

interface TaskDetailsModalProps {
  taskId: number
  onClose: () => void
}

interface ExtendedTask {
  id: number
  name: string
  workflowType: string
  workflowStepType?: string
  workflowDescription: string
  status: string
  startDate: number
  filesCount?: number
  assignee?: {
    id: number
    name: string
  }
  workflow: any
  files?: Array<{
    sourceFsi?: any
    draftFsi?: any
    targetFsi?: any
  }>
  initiatorEmail?: string
  assigneeEmail?: string
  initiatorName?: string
  userName?: string
  workflowFormattedId?: string
  workflowStatus?: string
  filename?: string
  initiatorId?: number
  eosDownloadMediaName?: string
  eosDownloadMediaId?: number
  endDate?: number
  reasonForReject?: string
  studyName?: string
  siteName?: string
  userId?: number
  canEditWorkflow?: boolean
  workflowId?: number
}

const TaskDetailsModal = ({ taskId, onClose }: TaskDetailsModalProps) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [loading, setLoading] = useState(true)
  const [task, setTask] = useState<ExtendedTask | null>(null)
  const [fileFullPath, setFileFullPath] = useState<string>('')

  useEffect(() => {
    const loadTaskDetails = async () => {
      try {
        // Get users and groups data
        const userData = await usersApiService.getAssembledUsersGroups()

        // Get task details with expand=true
        const taskData = await getTask(String(taskId))

        // Find initiator and assignee
        const initiator = userData.users.find((u: any) => u.id === (taskData as any).initiatorId)
        const assignedTo = userData.users.find((u: any) => u.id === taskData.userId)

        // Extract workflowId - it's directly on taskData object
        const workflowId = (taskData as any).workflowId || taskData.workflow?.id
        const workflowFormattedId = (taskData as any).workflowFormattedId

        // Build extended task with all properties
        const extendedTask: ExtendedTask = {
          ...(taskData as any),
          initiatorEmail: initiator?.email || '',
          assigneeEmail: assignedTo?.email || '',
          workflowFormattedId: workflowFormattedId,
          studyName: taskData.studyName || undefined,
          siteName: taskData.siteName || undefined,
          workflowId: workflowId
        }

        // Get workflow details if workflow exists
        if (workflowId) {
          const wfData = await getWorkflow(workflowId)
          extendedTask.workflow = wfData

          // Get file path if workflow has parent folder
          if (wfData.parentFolderId) {
            try {
              const pathData = await getFsiPath(wfData.parentFolderId)
              const fullPath = pathData.reduce((memo: string, item: any) => {
                return (memo ? memo + '/' : '') + item.name
              }, '')
              setFileFullPath(fullPath + '/')
            } catch (error) {
              console.error('Error fetching file path:', error)
              setFileFullPath('')
            }
          }
        }

        setTask(extendedTask)
        setLoading(false)
      } catch (error) {
        console.error('Error loading task details:', error)
        setLoading(false)
      }
    }

    loadTaskDetails()
  }, [taskId])

  return (
    <Modal
      className="theme-sandman"
      show={true}
      handleClose={onClose}
      title={translate('TASK_DETAILS')}
      size="md"
      style={{ overflow: 'visible' }}
    >
      <Modal.Body>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Loader blocking={loading} />
          </div>
        ) : task ? (
          <TaskDetailsTable fileFullPath={fileFullPath} task={task} onClose={onClose} />
        ) : (
          <div style={{ padding: '20px' }}>
            <p>No task data available</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-primary" onClick={onClose}>
          {translate('OK')}
        </button>
        {/* <Modal.Footer.Button onClick={onClose} variant="primary">
          {translate('OK')}
        </Modal.Footer.Button> */}
      </Modal.Footer>
    </Modal>
  )
}

export default TaskDetailsModal
